import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { Table, Tag, Button, Space, Spin, Empty, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { useAuth } from "../../../../hooks";
import { ReviResultsApi } from "../../../../api/revi/reviResultsApi";
import { useEvaluationChartsController } from "../../../../hooks/revi/useEvaluationChartsController";
import {
  EvaluationPicker,
  RadarProgressChart,
  ProgressionLineChart,
  SessionsBarChart,
} from "../EvaluationChartsCmp";
import { TherapistResultDetailModal } from "./TherapistResultDetailModal";
import { textoDeInterpretacion } from "../../../../Utils/interpretacionColor";
import "./TherapistPatientEvaluationsTab.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../revi/FilaTocable";
import { PaginacionCompacta } from "../../../revi/PaginacionCompacta";

const { Text } = Typography;
const api = new ReviResultsApi();

function scoreLabel(ps) {
  if (!ps) return "—";
  if (typeof ps === "number") return String(ps);
  if (typeof ps.total === "number") return String(ps.total);
  return ps.total === null ? "—" : "—";
}

function interpretationLabel(ps) {
  if (!ps) return "—";
  if (typeof ps?.interpretation === "string" && ps.interpretation.trim())
    return ps.interpretation;
  return "—";
}

export function TherapistPatientEvaluationsTab({ patientId, active }) {
  // "Fecha" y "Ver" ya estan en el bloque comun; el resto es de esta tabla.
  // "evaluations" entra por los rotulos de interpretacion, que se traducen
  // desde el codigo que emite el servidor.
  const { t } = useTranslation(["patient", "common", "evaluations"]);
  const { fechaHora } = useFormatos();

  const { accessToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalDocs: 0,
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [activeResultId, setActiveResultId] = useState(null);

  const {
    evaluationSummary,
    selectedEvaluationId,
    setSelectedEvaluationId,
    timeRange,
    setTimeRange,
    progression,
    latestResult,
    loading: chartsLoading,
    isEmpty,
  } = useEvaluationChartsController({ patientId });

  // Track whether patient has evaluations in any timeRange — keeps Picker
  // visible when a narrow filter (e.g. "7d") returns empty results.
  const everHadData = useRef(false);
  const prevPatientId = useRef(patientId);
  if (prevPatientId.current !== patientId) {
    everHadData.current = false;
    prevPatientId.current = patientId;
  }
  if (!isEmpty && !chartsLoading) {
    everHadData.current = true;
  }
  const showCharts = everHadData.current;

  const load = useCallback(
    async (page = 1) => {
      if (!accessToken || !patientId) return;

      try {
        setLoading(true);
        const res = await api.getPatientResultsForTherapist(
          accessToken,
          patientId,
          {
            page,
            limit: pagination.limit,
          },
        );

        setHistory(res.history || []);
        setPagination({
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || pagination.limit,
          totalDocs: res.pagination?.totalDocs || 0,
        });
      } catch (e) {
        console.error(e);
        setHistory([]);
        setPagination((p) => ({ ...p, totalDocs: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [accessToken, patientId, pagination.limit],
  );

  useEffect(() => {
    if (active) load(1);
  }, [active, load]);
  const movil = useMovil();

  const columns = useMemo(
    () => [
      {
        title: t("common:fields.date"),
        key: "sessionDate",
        render: (_, r) => fechaHora(r?.sessionDate),
      },
      {
        title: t("evaluaciones.tabla.evaluacion"),
        key: "evaluation",
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            <Text strong>{r?.evaluation?.title || "—"}</Text>
            <Space size={6}>
              {r?.evaluation?.subtype ? (
                <Tag>{r.evaluation.subtype}</Tag>
              ) : null}
              {r?.evaluation?.clinicalArea ? (
                <Tag>{r.evaluation.clinicalArea}</Tag>
              ) : null}
            </Space>
          </Space>
        ),
      },
      {
        title: t("evaluaciones.tabla.terapia"),
        key: "therapy",
        render: (_, r) => r?.therapy?.title || "—",
      },
      {
        title: t("evaluaciones.tabla.total"),
        key: "total",
        render: (_, r) => <Tag>{scoreLabel(r?.processedScore)}</Tag>,
      },
      {
        title: t("evaluaciones.tabla.interpretacion"),
        key: "interpretation",
        // Sin color a proposito: esta tabla es un historial y colorear cada
        // fila la convertiria en un semaforo. El color va en el detalle y en
        // la actividad reciente, donde se mira una sola.
        render: (_, r) => (
          <Text type="secondary">
            {textoDeInterpretacion(
              interpretationLabel(r?.processedScore),
              r?.processedScore?.interpretationCode,
              t,
            )}
          </Text>
        ),
      },
      {
        title: "",
        key: "actions",
        align: "right",
        render: (_, r) => (
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setActiveResultId(r._id);
              setDetailOpen(true);
            }}
          >
            {t("common:actions.view")}
          </Button>
        ),
      },
    ],
    [t, fechaHora],
  );

  const tablePagination = {
    current: pagination.page,
    pageSize: pagination.limit,
    total: pagination.totalDocs,
    onChange: (p) => load(p),
    showSizeChanger: false,
  };

  if (!active) return null;

  return (
    <div className="revi-patient-evals">
      {showCharts && (
        <div className="revi-patient-evals__charts">
          <EvaluationPicker
            evaluationSummary={evaluationSummary}
            selectedEvaluationId={selectedEvaluationId}
            onSelect={setSelectedEvaluationId}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            loading={chartsLoading}
          />
          {selectedEvaluationId ? (
            <div className="revi-patient-evals__charts-grid">
              <RadarProgressChart
                loading={chartsLoading}
                dimensions={latestResult?.processedScore?.dimensions ?? []}
              />
              <ProgressionLineChart
                loading={chartsLoading}
                history={progression.history}
              />
              <SessionsBarChart
                loading={chartsLoading}
                history={progression.history}
              />
            </div>
          ) : evaluationSummary.length > 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("evaluaciones.sinSeleccion")}
              style={{ padding: "24px 0" }}
            />
          ) : null}
        </div>
      )}

      {loading ? (
        <div className="revi-patient-evals__center">
          <Spin size="large" />
        </div>
      ) : history.length ? (
        movil ? (
          /*
            Seis columnas -fecha, evaluacion, terapia, total, interpretacion y
            accion- repartidas en 295 px dan columnas de una letra, y la de
            accion queda fuera de la pantalla sin forma de alcanzarla.

            En ficha, la identidad es el INSTRUMENTO y no la fecha: es lo que el
            terapeuta busca cuando repasa un historial. La fecha, el total y la
            interpretacion bajan a la meta. Nunca se pintan las dos ramas.
          */
          <div className="revi-patient-evals__fichas">
            {history.map((r) => (
              <FilaTocable
                key={r._id}
                alto={144}
                className="revi-eval-historial"
                aria-label={r?.evaluation?.title}
                onTocar={() => {
                  setActiveResultId(r._id);
                  setDetailOpen(true);
                }}
              >
                <div className="revi-eval-historial__texto">
                  <span className="revi-eval-historial__titulo">
                    {r?.evaluation?.title || "—"}
                  </span>

                  <div className="revi-eval-historial__meta">
                    <Tag>{scoreLabel(r?.processedScore)}</Tag>
                    <span>{fechaHora(r?.sessionDate)}</span>
                  </div>

                  <span className="revi-eval-historial__interpretacion">
                    {textoDeInterpretacion(
                      interpretationLabel(r?.processedScore),
                      r?.processedScore?.interpretationCode,
                      t,
                    )}
                  </span>
                </div>
              </FilaTocable>
            ))}

            {/* La lista de fichas no hereda la paginacion de la tabla, y el
                historial se sirve de diez en diez: sin esto, un paciente con
                doce evaluaciones tenia dos inalcanzables desde el movil. */}
            <PaginacionCompacta
              pagina={pagination.page}
              total={pagination.totalDocs}
              porPagina={pagination.limit}
              onCambiar={(p) => load(p)}
            />
          </div>
        ) : (
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={history}
            pagination={tablePagination}
          />
        )
      ) : (
        <div className="revi-patient-evals__center">
          <Empty description={t("evaluaciones.sinEvaluaciones")} />
        </div>
      )}

      <TherapistResultDetailModal
        open={detailOpen}
        resultId={activeResultId}
        onClose={() => {
          setDetailOpen(false);
          setActiveResultId(null);
        }}
      />
    </div>
  );
}
